import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Navbar, Footer } from '../components/Navbar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const draftKey = (memorialId) => `plaqueOrderDraft:${memorialId}`;

export default function PlaqueOrder() {
  const { memorialId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [productType, setProductType] = useState('acrylic_plaque');
  const [sizeId, setSizeId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [engraving, setEngraving] = useState('');
  const [qrArtworkUrl, setQrArtworkUrl] = useState('');
  const [memorial, setMemorial] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shipping, setShipping] = useState({
    shipping_full_name: '',
    shipping_email: user?.email || '',
    shipping_phone: '',
    shipping_address_line1: '',
    shipping_address_line2: '',
    shipping_city: '',
    shipping_state: '',
    shipping_postal_code: '',
    shipping_country: 'US',
    notes: '',
  });

  // Restore a saved draft (Stripe cancel returns here with ?cancelled=1).
  useEffect(() => {
    if (searchParams.get('cancelled')) {
      try {
        const saved = JSON.parse(localStorage.getItem(draftKey(memorialId)) || 'null');
        if (saved) {
          setProductType(saved.productType || 'acrylic_plaque');
          setSizeId(saved.sizeId || '');
          setQuantity(saved.quantity || 1);
          setEngraving(saved.engraving || '');
          setShipping((s) => ({ ...s, ...saved.shipping }));
          toast.info('Checkout was cancelled — your order details were saved. You have not been charged.');
        }
      } catch { /* ignore */ }
    }
    const prefillId = searchParams.get('prefill');
    if (prefillId) {
      api.get(`/qr-orders/${prefillId}/prefill`).then(({ data }) => {
        if (data.order_type === 'acrylic_plaque' || data.order_type === 'sticker') setProductType('acrylic_plaque');
        if (data.order_type === 'stainless_steel_plaque') setProductType('stainless_steel_plaque');
        setQuantity(data.quantity || 1);
        setShipping((s) => ({
          ...s,
          shipping_full_name: data.name || s.shipping_full_name,
          shipping_email: data.email || s.shipping_email,
          shipping_phone: data.phone || s.shipping_phone,
          shipping_address_line1: data.shipping_address || s.shipping_address_line1,
          notes: data.notes || s.notes,
        }));
        toast.info('We pre-filled your earlier request — review and complete secure checkout below.');
      }).catch(() => {});
    }
  }, [memorialId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    api.get('/plaque-orders/products').then(({ data }) => {
      setProducts(data.products || []);
      if ((data.products || []).length && !data.products.find((p) => p.id === productType)) {
        setProductType(data.products[0].id);
      }
    });
    api.get(`/memorials/${memorialId}`).then(({ data }) => {
      setMemorial(data);
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      setQrArtworkUrl(`${base}/api/memorials/${memorialId}/qr?format=png`);
    }).catch((e) => toast.error(formatApiError(e)));
  }, [memorialId]); // eslint-disable-line react-hooks/exhaustive-deps

  const product = products.find((p) => p.id === productType);
  const sizes = product?.sizes || [];
  const size = sizes.find((s) => s.id === sizeId);
  const unitPrice = size ? Number(size.price_usd) : Number(product?.price_usd || 0);
  const shippingUsd = Number(product?.shipping_usd || 0);
  const total = unitPrice * quantity + shippingUsd;

  const submit = async () => {
    setSubmitting(true);
    // Persist the draft so a Stripe cancel restores everything.
    localStorage.setItem(draftKey(memorialId), JSON.stringify({
      productType, sizeId, quantity, engraving, shipping,
    }));
    try {
      const { data } = await api.post('/plaque-orders/checkout', {
        memorial_id: memorialId,
        product_type: productType,
        size_id: sizeId || null,
        quantity: Number(quantity),
        engraving_text: engraving || null,
        ...shipping,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout.');
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setSubmitting(false);
    }
  };

  const shippingComplete = (
    shipping.shipping_full_name && shipping.shipping_email
    && shipping.shipping_address_line1 && shipping.shipping_city
    && shipping.shipping_state && shipping.shipping_postal_code
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="ml-container py-12 max-w-3xl">
        <button onClick={() => navigate(-1)} className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))] hover:underline mb-4" data-testid="plaque-back">← Back</button>
        <h1 className="font-serif text-4xl text-[hsl(var(--primary))]" data-testid="plaque-order-title">Order a QR plaque</h1>
        {memorial && (
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2">For <strong>{memorial.full_name}</strong></p>
        )}

        {/* Step 1 — Product */}
        <section className="mt-8 rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-6" data-testid="plaque-step-1">
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-4">1. Choose your product</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {products.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setProductType(p.id); setSizeId(''); }}
                className={`text-left p-5 rounded-xl border-2 transition ${
                  productType === p.id
                    ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5'
                    : 'border-[hsl(var(--primary)/0.18)] hover:border-[hsl(var(--accent))]/40'
                }`}
                data-testid={`plaque-product-${p.id}`}
              >
                {p.images?.[0] && (
                  <img src={p.images[0]} alt={p.label} className="w-full h-28 object-cover rounded-lg mb-3" />
                )}
                <div className="font-serif text-lg text-[hsl(var(--primary))]">{p.label}</div>
                <div className="text-xs text-[hsl(var(--primary)/0.7)] mt-1">{p.description}</div>
                <div className="font-medium text-[hsl(var(--accent))] mt-2" data-testid={`plaque-price-${p.id}`}>{money(p.price_usd)}</div>
              </button>
            ))}
          </div>
          {sizes.length > 0 && (
            <div className="mt-4">
              <label className="text-sm text-[hsl(var(--primary)/0.7)] block mb-2">Size</label>
              <div className="flex flex-wrap gap-2">
                {sizes.map((s) => (
                  <button key={s.id} type="button" onClick={() => setSizeId(s.id)}
                          data-testid={`plaque-size-${s.id}`}
                          className={`px-4 py-2 rounded-full border text-sm ${sizeId === s.id ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--primary))]' : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.7)]'}`}>
                    {s.label} · {money(s.price_usd)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="mt-4 flex items-center gap-3">
            <label className="text-sm text-[hsl(var(--primary)/0.7)]">Quantity</label>
            <input
              type="number" min={1} max={product?.max_quantity || 25}
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, Number(e.target.value || 1)))}
              className="ml-input w-24" data-testid="plaque-quantity"
            />
          </div>
        </section>

        {/* Step 2 — Confirm QR */}
        <section className="mt-6 rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-6" data-testid="plaque-step-2">
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-3">2. Confirm your QR artwork</h2>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mb-3">This QR will be printed/engraved on your plaque. Scanning it opens your loved one's memorial.</p>
          {qrArtworkUrl && (
            <div className="flex items-center justify-center bg-[hsl(var(--primary)/0.04)] rounded-xl p-6">
              <img src={qrArtworkUrl} alt="QR mockup" className="w-48 h-48 rounded-md" data-testid="plaque-qr-preview" />
            </div>
          )}
        </section>

        {/* Step 3 — Customization */}
        <section className="mt-6 rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-6" data-testid="plaque-step-customization">
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-3">3. Customization <span className="text-sm font-sans text-[hsl(var(--primary)/0.5)]">(optional)</span></h2>
          <textarea
            className="ml-input w-full" rows={3} maxLength={300}
            placeholder="Engraving text or customization details — e.g. name, dates, a short phrase…"
            value={engraving}
            onChange={(e) => setEngraving(e.target.value)}
            data-testid="plaque-engraving-input"
          />
        </section>

        {/* Step 4 — Shipping */}
        <section className="mt-6 rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-6" data-testid="plaque-step-3">
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-4">4. Shipping details</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <input className="ml-input" placeholder="Full name" value={shipping.shipping_full_name} onChange={(e) => setShipping({ ...shipping, shipping_full_name: e.target.value })} data-testid="plaque-shipping-name" />
            <input className="ml-input" placeholder="Email" type="email" value={shipping.shipping_email} onChange={(e) => setShipping({ ...shipping, shipping_email: e.target.value })} data-testid="plaque-shipping-email" />
            <input className="ml-input" placeholder="Phone (optional)" value={shipping.shipping_phone} onChange={(e) => setShipping({ ...shipping, shipping_phone: e.target.value })} data-testid="plaque-shipping-phone" />
            <input className="ml-input sm:col-span-2" placeholder="Address line 1" value={shipping.shipping_address_line1} onChange={(e) => setShipping({ ...shipping, shipping_address_line1: e.target.value })} data-testid="plaque-shipping-address1" />
            <input className="ml-input sm:col-span-2" placeholder="Address line 2 (optional)" value={shipping.shipping_address_line2} onChange={(e) => setShipping({ ...shipping, shipping_address_line2: e.target.value })} data-testid="plaque-shipping-address2" />
            <input className="ml-input" placeholder="City" value={shipping.shipping_city} onChange={(e) => setShipping({ ...shipping, shipping_city: e.target.value })} data-testid="plaque-shipping-city" />
            <input className="ml-input" placeholder="State / Region" value={shipping.shipping_state} onChange={(e) => setShipping({ ...shipping, shipping_state: e.target.value })} data-testid="plaque-shipping-state" />
            <input className="ml-input" placeholder="Postal code" value={shipping.shipping_postal_code} onChange={(e) => setShipping({ ...shipping, shipping_postal_code: e.target.value })} data-testid="plaque-shipping-zip" />
            <input className="ml-input" placeholder="Country" value={shipping.shipping_country} onChange={(e) => setShipping({ ...shipping, shipping_country: e.target.value })} data-testid="plaque-shipping-country" />
            <textarea className="ml-input sm:col-span-2" placeholder="Notes (optional)" value={shipping.notes} onChange={(e) => setShipping({ ...shipping, notes: e.target.value })} data-testid="plaque-shipping-notes" />
          </div>
        </section>

        {/* Order summary + Pay */}
        <div className="mt-8 rounded-2xl border border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5 p-6" data-testid="plaque-summary">
          <h2 className="font-serif text-2xl text-[hsl(var(--primary))] mb-3">5. Review &amp; pay</h2>
          <div className="text-sm text-[hsl(var(--primary)/0.8)] space-y-1.5">
            <div className="flex justify-between"><span>{product?.label || '—'}{size ? ` · ${size.label}` : ''} × {quantity}</span><span className="tabular-nums">{money(unitPrice * quantity)}</span></div>
            {shippingUsd > 0 && (
              <div className="flex justify-between"><span>Shipping</span><span className="tabular-nums">{money(shippingUsd)}</span></div>
            )}
            {engraving && (
              <div className="flex justify-between gap-4"><span>Customization</span><span className="text-right text-[hsl(var(--primary)/0.6)] italic truncate max-w-[220px]">"{engraving}"</span></div>
            )}
            <div className="flex justify-between border-t border-[hsl(var(--primary)/0.15)] pt-2 mt-2 font-semibold text-base">
              <span>Total due today</span>
              <span className="font-serif text-2xl text-[hsl(var(--primary))]" data-testid="plaque-total">{money(total)}</span>
            </div>
          </div>
          <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-[hsl(var(--primary)/0.6)] max-w-xs">
              Payment is processed securely by Stripe. Your order is placed only after successful payment.
            </p>
            <button
              type="button"
              onClick={submit}
              disabled={!shippingComplete || submitting || !product}
              className="ml-btn-primary px-8 py-3"
              data-testid="plaque-pay-btn"
            >
              {submitting ? 'Starting checkout…' : `Pay ${money(total)} with Stripe →`}
            </button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
